import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; // Import useNavigate
import { cn } from "@/lib/utils";
import { type LucideIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useListingFormContext } from "@/context/ListingFormContext"; // Import useListingFormContext
import { Button } from "@/components/ui/button"; // Import Button for consistent styling

interface NavLink {
  href: string;
  label: string;
  icon: LucideIcon;
}

interface DashboardNavProps {
  links: NavLink[];
  collapsed: boolean;
}

const DashboardNav = ({ links, collapsed }: DashboardNavProps) => {
  const location = useLocation();
  const navigate = useNavigate(); // Initialize useNavigate
  const { clearAllListingData } = useListingFormContext(); // Use the context
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <nav className="flex-1 py-4 overflow-y-auto">
      <ul className="space-y-1 px-2">
        {links.map((link) => {
          const isActive =
            location.pathname === link.href ||
            (link.href !== "/dashboard" &&
              link.href !== "/admin" &&
              location.pathname.startsWith(link.href));

          const isAddListingLink =
            link.href === "/dashboard/add-listing" ||
            link.href === "/admin/create-listing";

          if (isAddListingLink) {
            return (
              <li key={link.href}>
                <Button
                  onClick={() => {
                    clearAllListingData();
                    navigate(link.href);
                  }}
                  variant="ghost"
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 w-full justify-start transition-colors",
                    isActive
                      ? isAdminRoute
                        ? "bg-red-100 text-red-600 font-medium"
                        : "bg-propertylk-tag text-propertylk-blue font-medium"
                      : "text-gray-600 hover:bg-gray-100",
                    collapsed && "justify-center px-2"
                  )}
                >
                  {React.createElement(link.icon, { size: 20 })}
                  {!collapsed && <span>{link.label}</span>}
                </Button>
              </li>
            );
          }

          return (
            <li key={link.href}>
              <Link
                to={link.href}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 transition-colors",
                  isActive
                    ? isAdminRoute
                      ? "bg-red-100 text-red-600 font-medium"
                      : "bg-propertylk-tag text-propertylk-blue font-medium"
                    : "text-gray-600 hover:bg-gray-100",
                  collapsed && "justify-center px-2"
                )}
              >
                {React.createElement(link.icon, { size: 20 })}
                {!collapsed && <span>{link.label}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default DashboardNav;
